import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';

const TerminalEmulator: React.FC = () => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminal = useRef<Terminal | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);

  useEffect(() => {
    if (!terminalRef.current) return;

    // Initialize terminal
    terminal.current = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      theme: {
        background: '#1e1e1e',
        foreground: '#f0f0f0'
      }
    });
    
    fitAddon.current = new FitAddon();
    terminal.current.loadAddon(fitAddon.current);
    
    // Open terminal
    terminal.current.open(terminalRef.current);
    
    // Helper function to safely fit terminal
    const fitTerminal = () => {
      try {
        if (fitAddon.current && terminalRef.current &&
            terminalRef.current.offsetWidth > 0 &&
            terminalRef.current.offsetHeight > 0) {
          fitAddon.current.fit();
        }
      } catch (error) {
        console.error('Error fitting terminal:', error);
      }
    };
    
    // Helper function to add welcome message
    const addWelcomeMessage = () => {
      try {
        if (terminal.current) {
          terminal.current.writeln('Welcome to Terminal IDE!');
          terminal.current.writeln('Type commands below...\r\n');
        }
      } catch (error) {
        console.error('Error adding welcome message:', error);
      }
    };
    
    // Use requestAnimationFrame to ensure terminal is fully initialized
    const initTerminal = () => {
      requestAnimationFrame(() => {
        fitTerminal();
        addWelcomeMessage();
      });
    };
    
    initTerminal();
    
    // Handle container resize
    const handleResize = () => {
      fitTerminal();
    };
    
    const resizeObserver = new ResizeObserver(handleResize);
    if (terminalRef.current) {
      resizeObserver.observe(terminalRef.current);
    }
    
    return () => {
      if (terminalRef.current) {
        resizeObserver.unobserve(terminalRef.current);
      }
      resizeObserver.disconnect();
      if (terminal.current) {
        terminal.current.dispose();
      }
    };
  }, []);

  const [terminalMode, setTerminalMode] = useState('terminal');

  const handleModeChange = (mode: string) => {
    setTerminalMode(mode);
    // TODO: Implement mode switching logic
    console.log('Switching to mode:', mode);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-gray-800 p-2 flex justify-between items-center">
        <div className="text-sm text-gray-300">Terminal</div>
        <div>
          <select
            className="bg-gray-700 text-white text-sm px-2 py-1 rounded"
            value={terminalMode}
            onChange={(e) => handleModeChange(e.target.value)}
          >
            <option value="terminal">Terminal</option>
            <option value="shell">Shell</option>
            <option value="node">Node</option>
            <option value="python">Python</option>
          </select>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <div ref={terminalRef} className="h-full w-full" />
      </div>
    </div>
  );
};

export default TerminalEmulator;